import type { ReactNode } from "react";
import { Link } from "react-router";
import {
  AlertIcon,
  buttonClass,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  FileIcon,
  InstagramIcon,
  QrIcon,
  ReportsIcon,
  ShieldIcon,
  SparkleIcon,
  TeamIcon,
} from "../../ui/index.js";
import { siteText } from "./content/index.js";
import { type LegalKind, legalDocs } from "./legal.js";
import { SiteLayout, useSiteLang } from "./SiteLayout.js";
import { LEGAL_REVIEWED, LEGAL_UPDATED } from "./seller.js";

/** Контейнер секции сайта: та же ширина, что у шапки. */
function Section({ children, className = "", id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={`mx-auto w-full max-w-[1120px] px-4 lg:px-8 ${className}`}>
      {children}
    </section>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="mb-6 text-2xl font-semibold text-ink sm:text-3xl">{children}</h2>;
}

/** Иконки карточек «Что получаете», по порядку пунктов в `content`. */
const featureIcons = [CalendarIcon, SparkleIcon, TeamIcon, QrIcon, ReportsIcon, ShieldIcon];
const stepIcons = [InstagramIcon, FileIcon, SparkleIcon];

/** Макет переписки и записи в hero: те же компоненты, что в кабинете, а не картинка. */
function HeroDemo() {
  const t = siteText[useSiteLang()].demo;
  return (
    <div className="relative mx-auto w-full max-w-md lg:mx-0">
      <div className="rounded-sheet border border-line bg-white p-4 shadow-pop">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-ink-2">
          <InstagramIcon className="h-5 w-5" />
          {t.chatTitle}
        </div>
        <ol className="space-y-2">
          {t.messages.map((m) => (
            <li key={m.text} className={`flex ${m.from === "client" ? "justify-end" : "justify-start"}`}>
              <span
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-snug ${
                  m.from === "client" ? "rounded-br-md bg-brand text-white" : "rounded-bl-md bg-slate-100 text-ink"
                }`}
              >
                {m.text}
              </span>
            </li>
          ))}
        </ol>
      </div>
      <div className="-mt-5 ml-6 rounded-card border border-line bg-white p-3.5 shadow-pop sm:ml-12">
        <div className="mb-2 flex items-center gap-2 text-xs font-medium text-ink-3">
          <CalendarIcon className="h-4 w-4" />
          {t.bookingTitle}
        </div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold tabular-nums text-ink">
              {t.booking.time} <span className="font-medium">{t.booking.client}</span>
            </div>
            {/* Без `truncate`: строка без переносов раздувает ширину страницы на телефоне (полный снимок шире 390px). */}
            <div className="text-sm text-ink-2">{t.booking.details}</div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
            {t.booking.status}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Главная для гостя: что это, как работает, функции, цена, вопросы. Вошедший пользователь на `/` видит кабинет. */
export function LandingPage() {
  const t = siteText[useSiteLang()];
  return (
    <SiteLayout>
      <div className="bg-gradient-to-b from-page to-white">
        <Section className="grid items-center gap-10 pt-10 pb-10 sm:pt-14 lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:pt-20 lg:pb-12">
          <div>
            <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-ink sm:text-5xl">{t.hero.title}</h1>
            <p className="mt-5 max-w-xl text-lg text-ink-2">{t.hero.text}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/pricing" className={buttonClass("primary", "", "lg")}>
                {t.hero.cta}
              </Link>
              <Link to="/login" className={buttonClass("secondary", "", "lg")}>
                {t.hero.login}
              </Link>
            </div>
            <p className="mt-5 text-sm text-ink-3">{t.hero.note}</p>
          </div>
          <HeroDemo />
        </Section>
      </div>

      <Section className="pt-8 pb-6 sm:pt-10 sm:pb-8">
        <SectionHeading>{t.how.title}</SectionHeading>
        <ol className="grid gap-4 sm:grid-cols-3">
          {t.how.steps.map((s, i) => {
            const Icon = stepIcons[i] ?? SparkleIcon;
            return (
              <li key={s.title} className="rounded-card border border-line bg-white p-6">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-field bg-brand-soft text-brand">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="text-sm font-semibold text-ink-3">0{i + 1}</span>
                </div>
                <h3 className="text-lg font-semibold text-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-2">{s.text}</p>
              </li>
            );
          })}
        </ol>
      </Section>

      <div className="bg-page">
        <Section className="py-8 sm:py-10">
          <SectionHeading>{t.features.title}</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {t.features.items.map((f, i) => {
              const Icon = featureIcons[i] ?? CheckIcon;
              return (
                <div key={f.title} className="rounded-card border border-line bg-white p-6">
                  <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-field bg-brand-soft text-brand">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-semibold text-ink">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-ink-2">{f.text}</p>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      <Section className="pt-6 pb-12 sm:pt-8 sm:pb-16">
        <div className="grid items-center gap-8 rounded-sheet border border-line bg-white p-6 shadow-card sm:p-10 lg:grid-cols-[1fr_auto]">
          <div>
            <h2 className="text-2xl font-semibold text-ink sm:text-3xl">{t.pricingTeaser.title}</h2>
            <ul className="mt-5 space-y-2.5">
              {t.pricingTeaser.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-ink-2">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  {p}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-ink-3">{t.pricingTeaser.setup}</p>
          </div>
          <div className="flex flex-col items-start gap-4 lg:items-end">
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold tracking-tight text-ink">{t.pricingTeaser.price}</span>
              <span className="text-ink-2">{t.pricingTeaser.per}</span>
            </div>
            <Link to="/pricing" className={buttonClass("primary", "", "lg")}>
              {t.pricingTeaser.cta}
            </Link>
          </div>
        </div>
      </Section>

      <Section className="pb-12 sm:pb-16">
        <SectionHeading>{t.faq.title}</SectionHeading>
        <div className="divide-y divide-line rounded-card border border-line bg-white">
          {t.faq.items.map((item) => (
            <details key={item.q} className="group px-5 py-5 open:bg-page/60">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-ink [&::-webkit-details-marker]:hidden">
                {item.q}
                <ChevronDownIcon className="h-5 w-5 shrink-0 text-ink-3 transition group-open:rotate-180" />
              </summary>
              <p className="mt-3 max-w-2xl text-sm text-ink-2">{item.a}</p>
            </details>
          ))}
        </div>
      </Section>
    </SiteLayout>
  );
}

export function PricingPage() {
  const t = siteText[useSiteLang()].pricing;
  return (
    <Section className="py-12 sm:py-16">
      <h1 className="text-3xl font-semibold text-ink sm:text-4xl">{t.title}</h1>
      <p className="mt-3 max-w-2xl text-lg text-ink-2">{t.subtitle}</p>

      <div className="mt-10 grid gap-6 lg:grid-cols-[1.2fr_1fr] lg:items-start">
        <div className="rounded-sheet border-2 border-brand bg-white p-6 shadow-card sm:p-8">
          <div className="text-sm font-semibold uppercase tracking-wider text-brand">{t.plan}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-5xl font-bold tracking-tight text-ink">{t.price}</span>
            <span className="text-ink-2">{t.per}</span>
          </div>
          <ul className="mt-6 space-y-3">
            {t.points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-ink-2">
                <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                {p}
              </li>
            ))}
          </ul>
          <Link to="/login" className={buttonClass("primary", "mt-8 w-full sm:w-auto", "lg")}>
            {t.start}
          </Link>
        </div>

        <div>
          <h2 className="mb-4 text-xl font-semibold text-ink">{t.setupTitle}</h2>
          <div className="rounded-card border border-line bg-white p-5">
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-semibold text-ink">{t.self.title}</div>
              <div className="shrink-0 font-semibold text-brand">{t.self.price}</div>
            </div>
            <p className="mt-1.5 text-sm text-ink-2">{t.self.text}</p>
          </div>
        </div>
      </div>

      <div className="mt-10 space-y-2 border-t border-line pt-6 text-sm text-ink-3">
        <p>{t.vat}</p>
        <p>{t.mor}</p>
      </div>
    </Section>
  );
}

function LegalPage({ kind }: { kind: LegalKind }) {
  const lang = useSiteLang();
  const doc = legalDocs[lang][kind];
  const t = siteText[lang];
  return (
    <Section className="py-12 sm:py-16">
      <article className="max-w-3xl">
        {!LEGAL_REVIEWED && (
          <div className="mb-6 flex items-start gap-3 rounded-field bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertIcon className="mt-0.5 h-5 w-5 shrink-0" />
            {t.draft}
          </div>
        )}
        <h1 className="text-3xl font-semibold text-ink sm:text-4xl">{doc.title}</h1>
        <p className="mt-2 text-sm text-ink-3">
          {t.updated}: {LEGAL_UPDATED}
        </p>
        <p className="mt-1 text-sm text-ink-3">{doc.note}</p>
        <p className="mt-6 text-lg text-ink-2">{doc.intro}</p>
        {doc.sections.map((s) => (
          <section key={s.heading} id={s.id} className="mt-8 scroll-mt-20">
            <h2 className="mb-2 text-xl font-semibold text-ink">{s.heading}</h2>
            {s.paragraphs?.map((p) => (
              <p key={p} className="mb-3 leading-relaxed text-ink-2">
                {p}
              </p>
            ))}
            {s.list && (
              <ul className="list-disc space-y-1.5 pl-5 leading-relaxed text-ink-2">
                {s.list.map((li) => (
                  <li key={li}>{li}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>
    </Section>
  );
}

export const TermsPage = () => <LegalPage kind="terms" />;
export const RefundPage = () => <LegalPage kind="refund" />;
export const PrivacyPage = () => <LegalPage kind="privacy" />;
